/**
 * Annotation scanner — frontend mirror of `backend/src/lib/annotations.ts`
 * (spec 19). MUST stay byte-identical below the imports; parity-tested here.
 *
 * Content may be Markdown OR HTML (agent-turn persists Markdown verbatim;
 * agent-worker normalises via ensureHtml) — tokens are plain text, so raw
 * string matching works identically in both.
 */

export type AnnotationType = 'todo' | 'attachment' | 'legacy_attribution'

export interface Annotation {
  type: AnnotationType
  /** Inner payload: the TODO note text, or the attachment filename */
  text: string
  /** The full matched token, e.g. "[TODO: needs specifics]" */
  match: string
  start: number
  end: number
}

/**
 * Drafter gap markers: [TODO: <freeform note>]
 *
 * Bracket-aware: the payload may contain one level of balanced brackets, because
 * the drafter prompt (prompts/shared-blocks.ts, Hard Rule 3) instructs shapes
 * like `[TODO: company evidence needed — no [X] evidenced]`. A naive `[^\]]*`
 * stops at the inner `]` and leaves ` evidenced]` in evaluator-facing output.
 *
 * Newline-bounded: no part of a token may cross a `\n` — not the payload, and
 * not the gap after the `[TODO:` prefix. `[^\S\n]*` is "whitespace except
 * newline"; plain `\s*` would let a newline directly after the prefix re-open
 * the swallow below. An unclosed `[TODO:` must fail VISIBLE — the raw token
 * stays in the text — rather than running to the next `]` anywhere in the
 * document and silently eating real prose and citations.
 *
 * The two alternation branches are disjoint on their first character, so there
 * is no ambiguity to backtrack through.
 *
 * The colon-and-payload is OPTIONAL, so a bare `[TODO]` matches too. Two dev
 * drafts shipped a literal "[TODO]" into an exported document on 2026-08-19:
 * requiring the colon made a payload-less marker invisible to BOTH ends of
 * spec 19 — never stripped at export, and never counted by the editor's gap
 * scan, so the author saw no warning either. It is the same author-only token
 * with an empty note, and it must behave identically.
 *
 * Only the payload is optional — the closing `]` is not — so an unclosed
 * `[TODO` still fails VISIBLE rather than running into real prose. `[TODOS]`
 * does not match: after `TODO` the next character must be `:` or `]`.
 */
export const TODO_REGEX = /\[TODO(?::[^\S\n]*((?:[^[\]\n]|\[[^\]\n]*\])*))?\]/g
/** Attachment attribution tokens: [ATT: <filename>] — newline-bounded, see above */
export const ATT_REGEX = /\[ATT:[^\S\n]*([^\]\n]+)\]/g
/** Pre-token prose attribution — strip/count only, never emitted going forward */
export const LEGACY_ATTRIBUTION_REGEX = /\(from your attached documents?:[^\S\n]*([^)\n]+)\)/gi

const PATTERNS: Array<{ type: AnnotationType; regex: RegExp }> = [
  { type: 'todo', regex: TODO_REGEX },
  { type: 'attachment', regex: ATT_REGEX },
  { type: 'legacy_attribution', regex: LEGACY_ATTRIBUTION_REGEX },
]

export function findAnnotations(content: string): Annotation[] {
  const found: Annotation[] = []
  if (!content) return found
  for (const { type, regex } of PATTERNS) {
    // These patterns are exported and /g, so a caller may have left lastIndex
    // dirty via .test()/.exec(). matchAll clones the regex but COPIES lastIndex
    // into the clone, so without this reset the scan would start mid-string and
    // silently return nothing. Reset first; never assume the state is clean.
    regex.lastIndex = 0
    for (const m of content.matchAll(regex)) {
      found.push({
        type,
        text: (m[1] ?? '').trim(),
        match: m[0],
        start: m.index ?? 0,
        end: (m.index ?? 0) + m[0].length,
      })
    }
  }
  return found.sort((a, b) => a.start - b.start)
}

/**
 * Keep only annotations that no other annotation encloses.
 *
 * `TODO_REGEX` is bracket-aware, so an `[ATT: file.pdf]` can sit entirely
 * inside a TODO's payload. `findAnnotations` reports both — correctly, since
 * each is a real match — but every consumer that COUNTS or LISTS annotations
 * for the author (the backend's pre-export report, the editor's local gap scan)
 * wants the inner one treated as part of the author's note, not as a second
 * attribution the evaluator would have seen. Counting it reports one token
 * twice, and the two dialogs would disagree about the same draft.
 *
 * Lives beside the scanner rather than at each call site: the containment rule
 * is part of the scanner's contract, not a per-caller convention. (The editor's
 * decoration plugin enforces the same rule by a positional mechanism of its own
 * — it paints TODO first and skips ranges already covered.)
 *
 * Strictly-larger span, not `>=`, so two annotations with identical bounds
 * (impossible today; cheap insurance against a future pattern) cannot delete
 * each other and leave the caller with nothing.
 */
export function outermostAnnotations(annotations: Annotation[]): Annotation[] {
  return annotations.filter(
    (a) =>
      !annotations.some(
        (b) => b.start <= a.start && b.end >= a.end && b.end - b.start > a.end - a.start
      )
  )
}

/**
 * Marks where a token was removed, so residue cleanup fires only at real
 * removal sites, never on pre-existing prose.
 *
 * U+E000 is the FIRST Private Use codepoint — icon fonts and PDF custom-glyph
 * cmaps emit it, so text pasted out of a PDF viewer genuinely can carry one.
 * The sentinel is unique BY CONSTRUCTION rather than by assumption:
 * `stripAnnotations` removes every incoming copy before it writes any of its
 * own.
 */
const REMOVED = '\uE000'

export function stripAnnotations(content: string): string {
  if (!content) return content
  // Drop pre-existing sentinels FIRST. A stray U+E000 would otherwise act as a
  // removal marker and eat legitimate punctuation beside it. Doing it here also
  // makes the removal consistent — the char goes whether or not the content
  // turns out to hold an annotation.
  const source = content.replace(/\uE000/g, '')
  // No lastIndex reset needed here: String#replace with a /g regex sets
  // lastIndex to 0 itself before scanning (unlike matchAll — see above).
  const out = source
    .replace(TODO_REGEX, REMOVED)
    .replace(ATT_REGEX, REMOVED)
    .replace(LEGACY_ATTRIBUTION_REGEX, REMOVED)
  if (out === source) return source
  return out
    // A model that WRAPS an annotation in emphasis or a code span leaves the
    // delimiters behind once the body is gone: `[TODO: x]` collapsed to `` and
    // **[TODO: x]** to ****. Both shipped into a customer's exported DOCX on
    // 2026-08-19. Absorbing the wrapper HERE, before the residue rules, is also
    // what lets the standalone-sentence rule below fire at all — it needs the
    // sentinel adjacent to the terminator, and a delimiter sitting between the
    // two blocked it, so those exports carried a double full stop as well.
    //
    // Only a delimiter run that flanks the sentinel on BOTH sides is absorbed:
    // that is the case where the annotation was the ENTIRE span. Emphasis that
    // merely contains an annotation alongside real prose keeps its markers —
    // eating one half would unbalance the author's own formatting — and loses
    // only the space the removed token left behind (the last two rules, which
    // handle a space on either side of the marker).
    .replace(/(?<![`*_])([`*_]{1,3})\uE000\1(?![`*_])/g, REMOVED)
    .replace(/<(code|em|strong|b|i)>\uE000<\/\1>/gi, REMOVED)
    .replace(/[ \t]+\uE000(?=[`*_]|<\/)/g, REMOVED)
    .replace(/(?<=[`*_]|>)\uE000[ \t]+/g, REMOVED)
    // Annotations that stood as their own sentences: the sentence before them
    // had already ended, so every terminator they carried is residue.
    //
    // The group REPEATS so a RUN of such sentences collapses in one pass.
    // String#replace cannot overlap matches, so a single-group form consumes
    // the leading `[.!?]` of the first annotation, leaving the second one's
    // orphaned terminator with no sentence-end to anchor on. `end. [a]. [b].
    // Next` would then keep the double full stop this rule exists to remove.
    .replace(/([.!?])(?:[ \t]*(?:\uE000[ \t]*)+[.,;:!?])+(?=[ \t]|<|\r|\n|$)/g, '$1')
    .replace(/\uE000/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ ([.,;:!?])/g, '$1')
    .replace(/ (<\/[a-z][^>]*>)/g, '$1')
}
